import { useState, useEffect } from 'react';
import { adminStore } from '../../../store/adminStore';
import { games as defaultGames, type Game } from '../../../mocks/games';

export default function GamesEditor() {
  const [hiddenIds, setHiddenIds] = useState<string[]>([]);
  const [customGames, setCustomGames] = useState<Game[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const refresh = () => {
    const store = adminStore.getStore();
    setHiddenIds(store.hiddenGameIds);
    setCustomGames(store.customGames);
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener('bahnar-admin-updated', handler);
    return () => window.removeEventListener('bahnar-admin-updated', handler);
  }, []);

  const allGames = [...defaultGames, ...customGames];
  const visibleCount = allGames.filter((g) => !hiddenIds.includes(g.id)).length;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-bold text-gray-800">Danh sách Trò chơi</h2>
          <p className="text-xs text-gray-400">
            {visibleCount}/{allGames.length} trò chơi đang hiển thị
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Thêm trò chơi
        </button>
      </div>

      <div className="space-y-3">
        {allGames.map((game) => {
          const isHidden = hiddenIds.includes(game.id);
          const isCustom = customGames.some((cg) => cg.id === game.id);
          return (
            <div
              key={game.id}
              className={`bg-white rounded-2xl p-4 flex items-center gap-4 transition-all ${
                isHidden ? 'opacity-50' : ''
              }`}
            >
              <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                {game.image ? (
                  <img
                    src={game.image}
                    alt={game.title}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-gamepad-line text-gray-300 text-2xl"></i>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-bold text-gray-800 text-sm">{game.title}</h3>
                  {isCustom && (
                    <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full font-medium flex-shrink-0">
                      Mới thêm
                    </span>
                  )}
                  {isHidden && (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-400 rounded-full flex-shrink-0">
                      Đã ẩn
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 truncate">{game.shortDesc}</p>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <span className="text-xs text-gray-400">
                    <i className="ri-user-line mr-1"></i>
                    {game.players}
                  </span>
                  <span className="text-xs text-gray-400">
                    <i className="ri-time-line mr-1"></i>
                    {game.duration}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      game.category === 'Đồng đội'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {game.category}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => {
                    adminStore.toggleGameVisibility(game.id);
                    refresh();
                  }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                    isHidden
                      ? 'bg-orange-50 text-orange-500 hover:bg-orange-100'
                      : 'bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-400'
                  }`}
                >
                  <i className={isHidden ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                  {isHidden ? 'Hiện' : 'Ẩn'}
                </button>
                {isCustom && (
                  <button
                    onClick={() => {
                      adminStore.deleteCustomGame(game.id);
                      refresh();
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showAddModal && (
        <AddGameModal
          onClose={() => setShowAddModal(false)}
          onAdded={refresh}
        />
      )}
    </div>
  );
}

function AddGameModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState({
    title: '',
    shortDesc: '',
    description: '',
    image: '',
    players: '',
    duration: '',
    category: 'Đồng đội' as 'Đồng đội' | 'Cá nhân',
    rules: '',
    benefits: '',
    relatedKnowledge: '',
  });
  const [saved, setSaved] = useState(false);

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    const newGame: Game = {
      id: `custom-${Date.now()}`,
      title: form.title,
      shortDesc: form.shortDesc,
      description: form.description,
      image:
        form.image ||
        'https://readdy.ai/api/search-image?query=Traditional%20Bahnar%20ethnic%20folk%20game%20Vietnam%20children%20playing%20outdoor%2C%20warm%20earthy%20tones%2C%20cultural%20illustration%20style%2C%20simple%20clean%20background&width=400&height=300&seq=custom-game-img&orientation=landscape',
      chibiImage: '',
      players: form.players || '2–10 người',
      duration: form.duration || '5–10 phút',
      category: form.category,
      rules: form.rules.split('\n').filter(Boolean),
      howToPlay: [],
      benefits: form.benefits.split('\n').filter(Boolean),
      notes: [],
      relatedKnowledge: form.relatedKnowledge,
      countdownSeconds: 3,
    };
    adminStore.addCustomGame(newGame);
    onAdded();
    setSaved(true);
    setTimeout(onClose, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
        {saved ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-fill text-3xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Đã thêm trò chơi!</h3>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800">Thêm trò chơi mới</h3>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-50 cursor-pointer"
              >
                <i className="ri-close-line text-gray-400"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Tên trò chơi <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="VD: Nhảy dây Bahnar"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mô tả ngắn</label>
                <input
                  type="text"
                  value={form.shortDesc}
                  onChange={(e) => setForm((f) => ({ ...f, shortDesc: e.target.value }))}
                  placeholder="Một dòng mô tả ngắn về trò chơi"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mô tả đầy đủ</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">URL hình ảnh</label>
                <input
                  type="text"
                  value={form.image}
                  onChange={(e) => setForm((f) => ({ ...f, image: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Số người chơi</label>
                  <input
                    type="text"
                    value={form.players}
                    onChange={(e) => setForm((f) => ({ ...f, players: e.target.value }))}
                    placeholder="VD: 2–10 người"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Thời lượng</label>
                  <input
                    type="text"
                    value={form.duration}
                    onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                    placeholder="VD: 5–10 phút"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Loại trò chơi</label>
                <div className="flex gap-3">
                  {(['Đồng đội', 'Cá nhân'] as const).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setForm((f) => ({ ...f, category: cat }))}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${
                        form.category === cat
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'border-gray-200 text-gray-500 hover:border-orange-300'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Luật chơi
                  <span className="ml-1 font-normal text-gray-400 text-xs">(mỗi luật 1 dòng)</span>
                </label>
                <textarea
                  value={form.rules}
                  onChange={(e) => setForm((f) => ({ ...f, rules: e.target.value }))}
                  rows={3}
                  placeholder="Luật 1&#10;Luật 2&#10;Luật 3"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Lợi ích
                  <span className="ml-1 font-normal text-gray-400 text-xs">(mỗi lợi ích 1 dòng)</span>
                </label>
                <textarea
                  value={form.benefits}
                  onChange={(e) => setForm((f) => ({ ...f, benefits: e.target.value }))}
                  rows={3}
                  placeholder="Lợi ích 1&#10;Lợi ích 2"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.title.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-yellow-400 text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Thêm trò chơi
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
